import type { FunctionComponent } from 'react';
import { useEffect, useState } from 'react';
import _ from 'underscore';

import { Analytics } from '@trello/atlassian-analytics';
import { useMemberId } from '@trello/authentication';
import { sendErrorEvent } from '@trello/error-reporting';
import { forNamespace, forTemplate } from '@trello/legacy-i18n';
import { Button } from '@trello/nachos/button';
import { CloseIcon } from '@trello/nachos/icons/close';
import { Popover, usePopover } from '@trello/nachos/popover';
import { Spinner } from '@trello/nachos/spinner';
import { dangerouslyConvertPrivacyString } from '@trello/privacy';
import { navigate } from '@trello/router/navigate';
import type { AppManagementTestIds } from '@trello/test-ids';
import { getTestId } from '@trello/test-ids';

import { PopoverConfirm } from 'app/src/components/PopoverConfirm';
import type { PluginCollaboratorsQuery } from '../queries/PluginCollaboratorsQuery.generated';
import { usePluginCollaboratorsQuery } from '../queries/PluginCollaboratorsQuery.generated';
import type { PluginOrganizationMembersQuery } from '../queries/PluginOrganizationMembersQuery.generated';
import { usePluginOrganizationMembersQuery } from '../queries/PluginOrganizationMembersQuery.generated';
import { useRemovePluginCollaboratorMutation } from '../queries/RemovePluginCollaboratorMutation.generated';
import { AddCollaboratorsButton } from './AddCollaboratorsButton';

import styles from './MemberList.less';

const format = forTemplate('app_management');
const formatConfirm = forNamespace('confirm');

type Plugin = NonNullable<PluginCollaboratorsQuery['plugin']>;
type Organization = NonNullable<PluginOrganizationMembersQuery['organization']>;
type Members = NonNullable<Plugin['collaborators']>;
type Member = Members[number] | Organization['memberships'][number]['member'];

export const MemberItem: FunctionComponent<{
  idPlugin: string;
  idOrganizationOwner: string;
  member: Member;
  memberType: string;
  refetch?: () => void;
  refetching?: boolean;
  setRemoving?: (id: string) => void;
}> = ({
  idPlugin,
  idOrganizationOwner,
  member,
  memberType,
  refetch,
  refetching,
  setRemoving,
}) => {
  const memberId = useMemberId();
  const { id, fullName, username, initials, avatarUrl } = member;
  const isSelf = memberId === id;
  const { triggerRef, toggle, popoverProps, hide } =
    usePopover<HTMLButtonElement>();
  const [removePluginCollaboratorMutation, { loading }] =
    useRemovePluginCollaboratorMutation();

  const onRemove = async () => {
    try {
      Analytics.sendClickedButtonEvent({
        buttonName: 'powerUpAdminCollaboratorsRemoveButton',
        source: 'powerUpAdminCollaboratorsScreen',
        containers: {
          organization: { id: idOrganizationOwner },
        },
      });
      setRemoving?.(id);
      await removePluginCollaboratorMutation({
        variables: {
          idPlugin,
          idMember: id,
        },
      });
      if (isSelf) {
        navigate('/power-ups/admin', { trigger: true });
      }
      hide();
      refetch?.();
    } catch (err) {
      sendErrorEvent(err, {
        tags: {
          ownershipArea: 'trello-web-eng',
        },
        extraData: {
          component: 'PowerUpCollaboratorsMemberList',
          // @ts-expect-error
          errorCode: err?.networkError?.extensions?.code ?? 'UNKNOWN',
        },
      });
    }
  };

  return (
    <div
      className={styles.memberItem}
      data-testid={getTestId<AppManagementTestIds>(
        'power-up-collaborators-page-member-item',
      )}
    >
      <div className={styles.memberAvatar}>
        {avatarUrl ? (
          <img
            className={styles.memberImage}
            alt={memberType}
            src={[avatarUrl, '30.png'].join('/')}
            srcSet={
              [avatarUrl, '30.png 1x'].join('/') +
              ', ' +
              [avatarUrl, '50.png 2x'].join('/')
            }
          />
        ) : (
          <span className={styles.memberInitials}>{initials}</span>
        )}
      </div>
      <div className={styles.memberDetails}>
        <p className={styles.memberFullName}>{fullName}</p>
        <p
          className={styles.memberUsername}
        >{`@${dangerouslyConvertPrivacyString(username)}`}</p>
      </div>
      <div className={styles.memberOption}>
        {memberType === 'admin' ? format('admin') : format('collaborator')}
      </div>
      <div className={styles.memberOption}>
        <Button
          className={styles.memberRemoveButton}
          isDisabled={memberType === 'admin' || loading || refetching}
          isLoading={loading || refetching}
          ref={triggerRef}
          iconBefore={<CloseIcon />}
          onClick={() => (isSelf ? toggle() : onRemove())}
        >
          {isSelf ? format('leave') : format('remove')}
        </Button>
        {isSelf && (
          <Popover
            {...popoverProps}
            title={formatConfirm(['leave plugin collaborator', 'title'])}
          >
            <PopoverConfirm
              isDanger
              confirmKey={'leave plugin collaborator'}
              confirmCallback={() => onRemove()}
            />
          </Popover>
        )}
      </div>
    </div>
  );
};

export const MemberList: FunctionComponent<{
  idPlugin: string;
  idOrganizationOwner: string;
}> = ({ idPlugin, idOrganizationOwner }) => {
  const {
    data: memberData,
    error: memberError,
    loading: memberLoading,
  } = usePluginOrganizationMembersQuery({
    variables: {
      idOrganization: idOrganizationOwner,
    },
    waitOn: ['None'],
  });

  const {
    data: collaboratorData,
    error: collaboratorError,
    loading: collaboratorLoading,
    // eslint-disable-next-line @trello/no-apollo-refetch
    refetch,
  } = usePluginCollaboratorsQuery({
    notifyOnNetworkStatusChange: true,
    variables: {
      idPlugin,
    },
    waitOn: ['None'],
  });

  // We need to keep track of members who are in the process of being removed
  // so we can show a spinner for those members.
  // The loading value from the mutation is not enough b/c it doesn't capture the refetching time.
  const [membersBeingRemoved, setMembersBeingRemoved] = useState<string[]>([]);
  const setRemoving = (id: string) => {
    setMembersBeingRemoved(membersBeingRemoved.concat(id));
  };
  let content;

  if (memberError || collaboratorError || !idOrganizationOwner) {
    content = <div>{format('something-went-wrong')}</div>;
  }

  const memberships = memberData?.organization?.memberships;
  const collaborators = collaboratorData?.plugin?.collaborators;
  let refetching = false;

  useEffect(() => {
    // members that are done being removed won't be in the list of collaborators
    // so discard any members that aren't and keep the rest
    if (membersBeingRemoved.length && collaborators?.length) {
      const filtered = membersBeingRemoved.filter((id: string) => {
        return _.some(collaborators, (c) => c.id === id);
      });
      if (!_.isEqual(filtered, membersBeingRemoved)) {
        setMembersBeingRemoved(filtered);
      }
    }
  }, [membersBeingRemoved, collaborators]);

  if (memberLoading || !memberships || !collaborators) {
    content = (
      <div className={styles.spinnerContainer}>
        <Spinner
          testId={getTestId<AppManagementTestIds>(
            'power-up-collaborators-page-spinner',
          )}
        />
      </div>
    );
  } else {
    if (collaboratorLoading && collaborators) {
      refetching = true;
    }
    const isAdminOrDeactivated = (collaborator: Member) =>
      _.some(
        memberships,
        (membership) =>
          (membership.memberType === 'admin' || membership.deactivated) &&
          membership.member.id === collaborator.id,
      );
    const filteredCollaboratorMembers = collaborators.map((collaborator) => {
      // There's a legitimate scenario where a collaborator is also an admin.
      // In this case, we want them to be listed as an admin, so omit them
      if (isAdminOrDeactivated(collaborator)) {
        return null;
      }
      // In case of something gone wrong, a collaborator might not be a member of the org.
      // This shouldn't happen, but if it does, omit them from the list too.
      if (!collaborator.idOrganizations.includes(idOrganizationOwner)) {
        return null;
      }

      const isRefetching =
        membersBeingRemoved.includes(collaborator.id) && refetching;

      return (
        <MemberItem
          key={collaborator.id}
          idPlugin={idPlugin}
          idOrganizationOwner={idOrganizationOwner}
          member={collaborator}
          memberType={'normal'}
          refetch={refetch}
          refetching={isRefetching}
          setRemoving={setRemoving}
        />
      );
    });

    const adminMembers = memberships.map((membership) => {
      if (membership.memberType === 'admin' && !membership.deactivated) {
        const { member } = membership;
        return (
          <MemberItem
            key={member.id}
            idPlugin={idPlugin}
            idOrganizationOwner={idOrganizationOwner}
            member={member}
            memberType={'admin'}
          />
        );
      }
    });
    content = (
      <div>
        {filteredCollaboratorMembers}
        {adminMembers}
      </div>
    );
  }

  return (
    <>
      <div className={styles.addCollaboratorsButton}>
        <AddCollaboratorsButton
          idPlugin={idPlugin}
          collaborators={collaborators}
          memberships={memberships}
          idOrganizationOwner={idOrganizationOwner}
          refetch={refetch}
          refetching={refetching}
        />
      </div>
      <div className={styles.membersList}>{content}</div>
    </>
  );
};
